import '@ui5/webcomponents-icons/dist/open-folder';
import {
  Bar,
  BusyIndicator,
  Button,
  ComboBox,
  ComboBoxItem,
  DatePicker,
  FileUploader,
  FileUploaderDomRef,
  FlexBox,
  FlexBoxDirection,
  Label,
  ObjectPage,
  ObjectPageSection,
  ObjectPageTitle,
  Option,
  Select,
  TextArea,
  Title,
  Toast
} from '@ui5/webcomponents-react';
import { Loading } from 'components/shared/Loading';
import { TRAINING_PROJECT_NAME } from '../../../constants';
import { AttachmentToUpload } from 'hooks/useFilesForAttachments';
import { useGetDate } from 'hooks/useGetDate';
import { useShadowRootMount } from 'hooks/useShadowRootMount';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useABAPQueryStore } from 'stores/CreateABAPQueryStore';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useCorporateContentQueryStore } from 'stores/CreateCorporateContentQueryStore';
import { useDitaQueryStore } from 'stores/CreateDitaQueryStore';
import { useNonAbapQueryStore } from 'stores/CreateNonAbapQueryStore';
import { useTrainingQueryStore } from 'stores/CreateTrainingQueryStore';
import { filterAllowedTranslationTypes } from 'utils/filterAllowedTranslationTypes';
import { compareAlphabetically } from 'utils/sorting';
import { validateDeadline, validateProject, validateString } from 'utils/validator';
import {
  IssueTypeOptions,
  TranslationTypeOptions
} from '../../../stores/interfaces/CommonQueryState';
import { useStyles } from './CreateQueryView.jss';
import { CreateQueryViewProps } from './CreateQueryViewInterface';
import { CreateQueryHeaderContent } from './Header/CreateQueryHeaderContent';
import { useNotesQueryStore } from 'stores/CreateNotesQueryStore';
import { filterAllowedIssueTypes } from 'utils/filterAllowedIssueTypes';
import { useMarketingQueryStore } from 'stores/CreateMarketingQueryStore';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

import BarDesign from '@ui5/webcomponents/dist/types/BarDesign.js';
import BusyIndicatorSize from '@ui5/webcomponents/dist/types/BusyIndicatorSize.js';
import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export const CreateQueryView: React.FC<CreateQueryViewProps> = (props) => {
  const {
    // Toast
    toastMessage,
    toastDuration,
    toastOpen,
    setToastOpen,
    // Callbacks
    onClickCreate,
    onClickCreateAndNew,
    onClickCancel,
    resetFields,
    // Specific Query
    specificQuery
  } = props;

  const FILES_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.zip', '.pdf'];

  const { t } = useTranslation();
  const classes = useStyles();

  const store = useBaseQueryStore();
  const isSourceTextFrozen = useCreateQueryFreezeStore((freezeStore) => freezeStore.sourceText);
  const fetchingDita = useDitaQueryStore((ditaStore) => ditaStore.fetching);
  const fetchingNonAbap = useNonAbapQueryStore((nonAbapStore) => nonAbapStore.fetching);
  const fetchingTraining = useTrainingQueryStore((trainingStore) => trainingStore.fetching);
  const fetchingCorporateContent = useCorporateContentQueryStore(
    (corporateContentStore) => corporateContentStore.fetching
  );
  const fetchingAbap = useABAPQueryStore((abapStore) => abapStore.fetching);
  const fetchingNotes = useNotesQueryStore((notesStore) => notesStore.fetching);
  const fetchingMarketing = useMarketingQueryStore((marketingStore) => marketingStore.fetching);
  const fileUploaderRef = useRef<FileUploaderDomRef>(null);

  const [isLoading, setIsLoading] = useState(false);
  const { getFormattedDateString, getMinDeadline } = useGetDate();

  const distinctLanguagesSorted = Array.from(new Set(store.languages)).sort(compareAlphabetically);

  useShadowRootMount(fileUploaderRef, () => {
    if (fileUploaderRef.current && fileUploaderRef.current.shadowRoot) {
      const shadowRoot = fileUploaderRef.current.shadowRoot;
      const inputElement = shadowRoot.querySelector(
        '.ui5-file-uploader-input'
      ) as FileUploaderDomRef;
      if (inputElement) {
        inputElement.style.flexGrow = '1';
      }
    }
  });

  useEffect(() => {
    if (store.selectedTranslationType === TranslationTypeOptions.TRAINING) {
      selectProject(TRAINING_PROJECT_NAME);
    }
  }, [store.projectNames?.data?.projects]);

  useEffect(() => {
    const fetching =
      fetchingDita ||
      fetchingNonAbap ||
      fetchingTraining ||
      fetchingCorporateContent ||
      fetchingAbap ||
      fetchingNotes ||
      fetchingMarketing;
    setIsLoading(fetching);
  }, [
    fetchingDita,
    fetchingNonAbap,
    fetchingTraining,
    fetchingCorporateContent,
    fetchingAbap,
    fetchingNotes,
    fetchingMarketing
  ]);

  const onChangeTranslationType = (event: CustomEvent) => {
    const { value } = event.detail.selectedOption.dataset;
    resetFields();
    store.setSelectedTranslationType(value);

    if (value === TranslationTypeOptions.NOT_SELECTED_TRANSLATION_TYPE) {
      store.setTranslationTypeValueState(ValueState.Negative);
      return;
    }

    store.setTranslationTypeValueState(ValueState.None);
  };

  const onChangeTargetLanguage = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;

    const selectedProject = store.projectNames?.data?.projects.find(
      (project) => project.id === store.selectedProject.toString()
    );
    const selectedLanguage = selectedProject?.languages.find(
      (projectLanguages) => projectLanguages?.language === value
    );

    if (selectedLanguage) {
      store.setTargetLanguageValueState(ValueState.Positive);
      store.setTargetLanguage(value);
    } else {
      store.setTargetLanguageValueState(ValueState.Negative);
      store.setTargetLanguage('');
    }
  };

  const onChangeProject = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    selectProject(value);
  };

  const selectProject = (projectName: string) => {
    const newSelectedProject = store.projectNames?.data?.projects.find((project) => {
      return project.projectName === projectName;
    });

    store.setTargetLanguage('');
    store.setTargetLanguageValueState(ValueState.None);
    store.setLanguages(
      newSelectedProject?.languages.map((projectLanguage) => {
        return projectLanguage?.language;
      }) || []
    );

    const projectId = newSelectedProject?.id ? Number(newSelectedProject.id) : -1;
    store.setSelectedProject(projectId);
    validateProject(projectId.toString(), store.setProjectValueState);
  };

  const findProjectNameById = (projectId: number): string => {
    const stringId = projectId.toString();
    const foundProject = store.projectNames?.data?.projects.find((proj) => {
      return proj.id === stringId;
    });
    return foundProject?.projectName ?? foundProject?.name ?? '';
  };

  const onChangeDeadline = (event: CustomEvent) => {
    event.preventDefault();
    const { value, valid } = event.detail as any;
    store.setDeadline(value);
    store.setAnswerDeadline(value);
    validateDeadline(store.setDeadlineValueState, value, valid);
  };

  const onInputDeadline = (event: CustomEvent) => {
    event.preventDefault();
    const { value, valid } = event.detail as any;
    validateDeadline(store.setDeadlineValueState, value, valid);
  };

  const onChangeSourceText = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setSourceText(value);
    validateString(value, store.setSourceTextValueState);
  };

  const onChangeTargetText = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setTargetText(value);
  };

  const onChangeDetails = (event: CustomEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    store.setDetails(value);
  };

  const onChangeIssueType = (event: CustomEvent) => {
    const { value } = event.detail.selectedOption.dataset;
    store.setSelectedIssueType(value);
    if (value === IssueTypeOptions.NOT_SELECTED_ISSUE_TYPE) {
      store.setIssueTypeValueState(ValueState.Negative);
    } else {
      validateString(value, store.setIssueTypeValueState);
    }
  };

  const onAddAttachments = (event: CustomEvent) => {
    const { files } = event.target as any;
    const fileList: AttachmentToUpload[] = Array.from(files);
    store.setAttachments(fileList);
  };

  if (isLoading) {
    return <Loading size={BusyIndicatorSize.L} />;
  }

  const isTranslationTypeSelected =
    store.selectedTranslationType !== TranslationTypeOptions.NOT_SELECTED_TRANSLATION_TYPE;

  const isProjectSelected = store.selectedProject !== -1;

  const getValidLanguage = (language: string): string => {
    return store.languages.includes(language) ? language : '';
  };

  const getInitialProjectValue = (): string => {
    if (!isTranslationTypeSelected) {
      return t('selection-of-project-without-translation-type');
    }

    if (!isProjectSelected) {
      return '';
    }

    return findProjectNameById(store.selectedProject);
  };

  return (
    <div className={classes.root}>
      <ObjectPage
        className={classes.objectPage}
        titleArea={<ObjectPageTitle header={<Title>{t('create-query-title')}</Title>} />}
        headerArea={<CreateQueryHeaderContent />}
        headerPinned
        footerArea={
          <Bar
            design={BarDesign.FloatingFooter}
            endContent={
              <>
                <Button
                  data-help-id="create-query-button-create"
                  design={ButtonDesign.Emphasized}
                  onClick={onClickCreate}
                >
                  {t('create')}
                </Button>
                <Button
                  data-help-id="create-query-button-create-and-new"
                  design={ButtonDesign.Emphasized}
                  onClick={onClickCreateAndNew}
                >
                  {t('create-and-new')}
                </Button>
                <Button
                  data-help-id="create-query-button-cancel"
                  design={ButtonDesign.Default}
                  onClick={onClickCancel}
                >
                  {t('cancel')}
                </Button>
              </>
            }
          />
        }
        hidePinButton
      >
        <ObjectPageSection
          id="general"
          aria-label={t('create-query-general-information-title')}
          titleText={t('create-query-general-information-title')}
          className={isTranslationTypeSelected ? classes.container : classes.lastContainer}
        >
          <FlexBox direction={FlexBoxDirection.Column}>
            <div className={classes.div}>
              <Label
                id="translation-type-label"
                className={classes.label}
                showColon
                required
                wrappingType="None"
              >
                <span data-help-id="create-query-translation-type">
                  {t('translation-type-label')}
                </span>
              </Label>
              <Select
                aria-labelledby="translation-type-label"
                className={classes.formLineInput}
                onChange={onChangeTranslationType}
                valueState={store.translationTypeValueState}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('display-value-state-message-translation-type')}
                  </Label>
                }
              >
                {Object.values(TranslationTypeOptions)
                  .filter(filterAllowedTranslationTypes)
                  .sort((a, b) => compareAlphabetically(t(a), t(b)))
                  .map((translationType) => (
                    <Option
                      key={translationType}
                      data-value={translationType}
                      selected={translationType === store.selectedTranslationType}
                      data-testid="translation-type-option"
                    >
                      {t(translationType)}
                    </Option>
                  ))}
              </Select>
            </div>
          </FlexBox>
          <FlexBox direction={FlexBoxDirection.Row}>
            <div className={[classes.div, classes.formLineInput].join(' ')}>
              <Label
                id="project-name-label"
                className={classes.label}
                showColon
                required
                wrappingType="None"
              >
                <span data-help-id="create-query-project-name">{t('project-name-label')}</span>
              </Label>
              <BusyIndicator
                active={store.projectNames?.fetching}
                className={classes.formLineInput}
                delay={0}
                size={BusyIndicatorSize.S}
              >
                <ComboBox
                  aria-labelledby="project-name-label"
                  className={classes.formLineInput}
                  onChange={onChangeProject}
                  filter="Contains"
                  value={getInitialProjectValue()}
                  disabled={!isTranslationTypeSelected}
                  valueState={store.projectValueState}
                  valueStateMessage={
                    <Label wrappingType={WrappingType.Normal}>
                      {t('display-value-state-message-project-name')}
                    </Label>
                  }
                >
                  {store.projectNames?.data?.projects.map((project) => (
                    <ComboBoxItem id={project.id} key={project.id} text={project.projectName} />
                  ))}
                </ComboBox>
              </BusyIndicator>
            </div>
          </FlexBox>
          <FlexBox direction={FlexBoxDirection.Column}>
            <div className={classes.div}>
              <Label
                id="target-language-label"
                className={classes.label}
                showColon
                required
                wrappingType="None"
              >
                <span data-help-id="create-query-target-language">
                  {t('target-language-label')}
                </span>
              </Label>
              <ComboBox
                aria-labelledby="target-language-label"
                className={classes.formInput}
                onChange={onChangeTargetLanguage}
                value={
                  isProjectSelected
                    ? getValidLanguage(store.targetLanguage)
                    : t('target-language-without-project')
                }
                valueState={store.targetLanguageValueState}
                disabled={!isProjectSelected}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('display-value-state-message-target-language')}
                  </Label>
                }
              >
                {distinctLanguagesSorted.map((language) => (
                  <ComboBoxItem key={language} text={language} data-testid="target-language-item" />
                ))}
              </ComboBox>
              <Label
                id="deadline-label"
                className={classes.secondColumnLabel}
                showColon
                required
                wrappingType="None"
              >
                <span data-help-id="create-query-deadline">{t('deadline-label')}</span>
              </Label>
              <div className={classes.formBusyIndicator}>
                <BusyIndicator active={store.projectDetails?.fetching}>
                  <DatePicker
                    aria-labelledby="deadline-label"
                    valueState={store.deadlineValueState}
                    valueStateMessage={
                      <Label wrappingType={WrappingType.Normal}>
                        {t('create-value-state-message-deadline')}
                      </Label>
                    }
                    value={store.answerDeadline}
                    onChange={onChangeDeadline}
                    onInput={onInputDeadline}
                    minDate={getFormattedDateString(getMinDeadline())}
                    displayFormat="MMM d, y"
                    valueFormat="MMM d, y"
                    required={true}
                  />
                </BusyIndicator>
              </div>
            </div>
          </FlexBox>
          <FlexBox direction={FlexBoxDirection.Row}>
            <div className={[classes.div, classes.formLineInput].join(' ')}>
              <Label
                id="issue-type-label"
                className={classes.label}
                showColon
                required
                wrappingType="None"
              >
                <span data-help-id="create-query-issue-type">{t('issue-type-label')}</span>
              </Label>
              <Select
                aria-labelledby="issue-type-label"
                className={classes.formLineInput}
                onChange={onChangeIssueType}
                valueState={store.issueTypeValueState}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('display-value-state-message-issue-type')}
                  </Label>
                }
                title={t(store.selectedIssueType)}
              >
                {Object.values(IssueTypeOptions)
                  .filter(filterAllowedIssueTypes(store.selectedTranslationType))
                  .sort((a, b) => compareAlphabetically(t(a), t(b)))
                  .map((issueType) => (
                    <Option
                      key={issueType}
                      data-value={issueType}
                      selected={issueType === store.selectedIssueType}
                      data-testid="issue-type-option"
                    >
                      {t(issueType)}
                    </Option>
                  ))}
              </Select>
            </div>
          </FlexBox>
          <FlexBox direction={FlexBoxDirection.Column}>
            <div className={classes.div}>
              <Label
                id="source-text-label"
                className={classes.textAreaLabel}
                required
                showColon
                wrappingType="None"
              >
                <span data-help-id="create-query-source-text">{t('source-text-label')}</span>
              </Label>
              <TextArea
                aria-labelledby="source-text-label"
                valueState={store.sourceTextValueState}
                valueStateMessage={
                  <Label wrappingType={WrappingType.Normal}>
                    {t('display-value-state-message-source-text')}
                  </Label>
                }
                onChange={onChangeSourceText}
                value={store.sourceText}
                rows={4}
                disabled={isSourceTextFrozen}
              />
            </div>
            <div className={classes.div}>
              <Label
                id="target-text-label"
                className={classes.textAreaLabel}
                showColon
                wrappingType="None"
              >
                <span data-help-id="create-query-target-text">{t('target-text-label')}</span>
              </Label>
              <TextArea
                aria-labelledby="target-text-label"
                onChange={onChangeTargetText}
                value={store.targetText}
                valueState={ValueState.None}
                rows={4}
              />
            </div>
            <div className={classes.div}>
              <Label
                id="details-label"
                className={classes.textAreaLabel}
                showColon
                wrappingType="None"
              >
                <span data-help-id="create-query-details">{t('details-label')}</span>
              </Label>
              <TextArea
                aria-labelledby="details-label"
                onChange={onChangeDetails}
                value={store.details}
                valueState={ValueState.None}
                rows={4}
              />
            </div>
            <div className={classes.div}>
              <Label id="attachments-label" className={classes.label} showColon wrappingType="None">
                <span data-help-id="create-query-attachments">
                  {t('create-query-attachments-label')}
                </span>
              </Label>
              <FileUploader
                placeholder={t('create-query-attachments-no-files')}
                multiple
                aria-labelledby="attachments-label"
                className={classes.formLineInput}
                ref={fileUploaderRef}
                accept={FILES_EXTENSIONS.join(',')}
                onChange={onAddAttachments}
                value={store.attachments.map((attachment) => `"${attachment.name}"`).join(', ')}
                valueState={store.attachmentValueState}
                valueStateMessage={<span>{store.attachmentValueStateMessage}</span>}
              >
                <Button icon="open-folder">{t('create-query-attachments-upload-file')}</Button>
              </FileUploader>
            </div>
          </FlexBox>
        </ObjectPageSection>

        {isTranslationTypeSelected && (
          <ObjectPageSection
            id="specific"
            aria-label={t('create-query-translation-specific-title')}
            titleText={t('create-query-translation-specific-title')}
            titleTextUppercase={false}
          >
            <div className={classes.lastContainer}>{specificQuery}</div>
          </ObjectPageSection>
        )}
      </ObjectPage>
      <Toast
        data-testid="create-query-toast"
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        duration={toastDuration}
      >
        {toastMessage}
      </Toast>
    </div>
  );
};
